import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref as dbRef, get } from 'firebase/database';
import { useSearchParams } from 'react-router-dom';
import './AnalysisPage.css';
import { useUsersData } from "../../Helpers/UsersContext";
import { BuildingType } from "../../types/BuildingTypeEnums";
import { BuildingSummary } from "../../types/protocols";
import ServicesSummarySection from './ServicesSummarySection';
import BuildingLogsModal from './components/BuildingLogsModal';
import {
    BuildingTypeOption,
    DateRange,
    ServiceData,
    ServiceLogEntry,
    ServiceType,
    TimeGrouping, UserGroup
} from "./types/analysis.types";
import {getDefaultDateRange} from "./utils/dateUtils";
import {
    extractIndividualVisits,
    extractServiceLogEntries,
    getCollectedValuesSummary,
    getServicesSummary,
    transformDataForChart
} from "./utils/dataProcessing";
import AnalysisControls from "./components/AnalysisControls";
import { buildingFilterState, NONE_MATCH_MESSAGE, unmatchedNote } from "./utils/buildingFilter";
import AnalysisChart from "./components/AnalysisChart";
import {SERVICE_COLORS} from "./utils/constants";
import DataTablesSection from "./components/DataTablesSection";
import YearComparisonView from "./components/YearComparisonView";
import RoutesView from "./components/RoutesView";
import { BuildingServicesData } from '../../hooks/analysis/useYearComparison';
import COLORS from "../../Helpers/utils";
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useHasServiceTypes } from '../../hooks/useHasServiceTypes';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useBuildingTypeFilterTypes } from '../../hooks/useEnabledBuildingTypes';
import { buildingTypeLabelOptions } from '../../Helpers/buildingTypeOptions';

// Define sort options type
type SortOption = 'visits' | 'sacks' | 'tanks' | 'cleaning' | 'solarControl' | 'deposit' | 'wasteBarrels' | 'compost';

type AnalysisView = 'overview' | 'compare' | 'routes';
const VIEW_PARAM = 'vy';
/** The ?vy= value of each tab but Översikt */
const VIEW_PARAMS: Record<Exclude<AnalysisView, 'overview'>, string> = {
    compare: 'jamfor',
    routes: 'resvagar',
};

/** Resvägar is built but not released: no tab, and ?vy=resvagar opens Översikt. Set to true to release it. */
const SHOW_ROUTES_TAB = false;

const viewFromParam = (param: string | null): AnalysisView => {
    const view = (Object.keys(VIEW_PARAMS) as Exclude<AnalysisView, 'overview'>[]).find(key => VIEW_PARAMS[key] === param);
    if (!view || (view === 'routes' && !SHOW_ROUTES_TAB)) return 'overview';
    return view;
};

// The service analysis covers the toilet types only
const ANALYSIS_BUILDING_TYPES: readonly BuildingType[] = [BuildingType.toiletArchipelago, BuildingType.toiletReserve];

const AnalysisPage: React.FC = () => {
    // Set document title
    useDocumentTitle('Analys');

    // The analysis is only meaningful when the instance has service types
    // configured; other instances get an empty state and no data fetch.
    const hasServiceTypes = useHasServiceTypes();

    // State management
    const [serviceData, setServiceData] = useState<ServiceData[]>([]);
    const [logEntries, setLogEntries] = useState<ServiceLogEntry[]>([]);
    const [commentEntries, setCommentEntries] = useState<ServiceLogEntry[]>([]);
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingType[]>([]);
    const [selectedBuildingTypeOptions, setSelectedBuildingTypeOptions] = useState<BuildingTypeOption[]>([]);
    const [selectedUserGroup, setSelectedUserGroup] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
    const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
    const [serviceTitles, setServiceTitles] = useState<Record<string, string>>({});
    const [timeGrouping, setTimeGrouping] = useState<TimeGrouping>('weekly');
    const [buildings, setBuildings] = useState<BuildingSummary[]>([]);
    // Byggnader: picked building keys, empty = all buildings
    const [selectedBuildingKeys, setSelectedBuildingKeys] = useState<string[]>([]);

    // The fetched buildingServices node, every year; the Jämför år tab derives from it
    const [servicesData, setServicesData] = useState<BuildingServicesData | null>(null);

    // Översikt | Jämför år | Resvägar, in the URL (?vy=jamfor, ?vy=resvagar) so a reload or shared link keeps it
    const [searchParams, setSearchParams] = useSearchParams();
    const view = viewFromParam(searchParams.get(VIEW_PARAM));
    const selectView = (next: AnalysisView) => {
        setSearchParams(prev => {
            const updated = new URLSearchParams(prev);
            if (next === 'overview') updated.delete(VIEW_PARAM);
            else updated.set(VIEW_PARAM, VIEW_PARAMS[next]);
            return updated;
        }, { replace: true });
    };


    // The toilet types, as far as the instance offers them (enabled or still in use)
    const typesInUse = useMemo(() => buildings.map(building => building.buildingType), [buildings]);
    const filterTypes = useBuildingTypeFilterTypes(typesInUse);
    const buildingTypeOptions: BuildingTypeOption[] = useMemo(
        () => buildingTypeLabelOptions(ANALYSIS_BUILDING_TYPES.filter(type => filterTypes.includes(type))),
        [filterTypes],
    );
    const visibleSelectedBuildingTypeOptions = useMemo(
        () => selectedBuildingTypeOptions.filter(option => filterTypes.includes(option.value)),
        [selectedBuildingTypeOptions, filterTypes],
    );
    // Byggnader: offered and picked buildings. Every filter must match; a picked
    // building another filter excludes stays picked, greyed, and counts for nothing
    const buildingFilter = useMemo(
        () => buildingFilterState(buildings, selectedBuildingTypes, selectedUserGroup, selectedBuildingKeys),
        [buildings, selectedBuildingTypes, selectedUserGroup, selectedBuildingKeys],
    );
    const [servicesConfigs, setServicesConfigs] = useState<Record<string, any>>({});
    const [individualVisits, setIndividualVisits] = useState<ServiceLogEntry[]>([]);
    const { users } = useUsersData();
    // Stable while loaded, so Resvägar derives its routes only when the users change
    const userList = useMemo(() => users || [], [users]);
    const isAdmin = useIsAdmin();
    // Bumped after an admin corrects a value, to refetch the logs
    const [reloadCount, setReloadCount] = useState(0);
    const [userGroups, setUserGroups] = useState<UserGroup[]>([]);

    // New state for combined toplist
    const [sortBy, setSortBy] = useState<SortOption>('visits');
    const [isToplistCollapsed, setIsToplistCollapsed] = useState<boolean>(true);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedBuilding, setSelectedBuilding] = useState<BuildingSummary | null>(null);

    // Combined toplist computation
    const getCombinedTopList = () => {
        const buildingStats: Record<string, {
            visits: number;
            sacks: number;
            tanks: number;
            cleaning: number;
            solarControl: number;
            deposit: number;
            wasteBarrels: number;
            compost: number;
            name: string
        }> = {};

        // Initialize all buildings
        buildings.forEach(building => {
            buildingStats[building.key] = {
                visits: 0,
                sacks: 0,
                tanks: 0,
                cleaning: 0,
                solarControl: 0,
                deposit: 0,
                wasteBarrels: 0,
                compost: 0,
                name: building.name || building.key
            };
        });

        // Count visits
        individualVisits.forEach(entry => {
            if (buildingStats[entry.buildingKey]) {
                buildingStats[entry.buildingKey].visits++;
            }
        });

        // Count all service types
        logEntries.forEach(entry => {
            if (buildingStats[entry.buildingKey]) {
                // Find the service type configuration to check if it collects values
                const serviceType = serviceTypes.find(st => st.key === entry.serviceType);
                const collectValue = serviceType?.collectValue ?? false;

                switch (entry.serviceType) {
                    case 'collectGarbage':
                        buildingStats[entry.buildingKey].sacks += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'toiletPumpout':
                        buildingStats[entry.buildingKey].tanks += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'generalCleaning':
                        buildingStats[entry.buildingKey].cleaning += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'solarCheckTankAndFan':
                        buildingStats[entry.buildingKey].solarControl += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'collectCansSeparately':
                        buildingStats[entry.buildingKey].deposit += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'handleWasteBarrels':
                        buildingStats[entry.buildingKey].wasteBarrels += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'handleCompost':
                        buildingStats[entry.buildingKey].compost += collectValue ? (entry.value ?? 0) : 1;
                        break;
                }
            }
        });

        // Convert to array and filter out buildings with no data
        const topList = Object.entries(buildingStats)
            .map(([key, stats]) => ({ key, ...stats }))
            .filter(item => item.visits > 0 || item.sacks > 0 || item.tanks > 0 || item.cleaning > 0 || item.solarControl > 0 || item.deposit > 0 || item.wasteBarrels > 0 || item.compost > 0);

        // Sort based on selected criteria
        switch (sortBy) {
            case 'visits':
                return topList.sort((a, b) => b.visits - a.visits);
            case 'sacks':
                return topList.sort((a, b) => b.sacks - a.sacks);
            case 'tanks':
                return topList.sort((a, b) => b.tanks - a.tanks);
            case 'cleaning':
                return topList.sort((a, b) => b.cleaning - a.cleaning);
            case 'solarControl':
                return topList.sort((a, b) => b.solarControl - a.solarControl);
            case 'deposit':
                return topList.sort((a, b) => b.deposit - a.deposit);
            case 'wasteBarrels':
                return topList.sort((a, b) => b.wasteBarrels - a.wasteBarrels);
            case 'compost':
                return topList.sort((a, b) => b.compost - a.compost);
            default:
                return topList;
        }
    };

    const combinedTopList = getCombinedTopList();

    const toggleToplist = () => setIsToplistCollapsed(!isToplistCollapsed);

    // Modal functions
    const handleBuildingClick = (buildingKey: string) => {
        const building = buildings.find(b => b.key === buildingKey);
        if (building) {
            window.open(`building/${buildingKey}`)
        }
    };

    const handleBuildingLogClick = (buildingKey: string) => {
        const building = buildings.find(b => b.key === buildingKey);
        if (building) {
            setSelectedBuilding(building);
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedBuilding(null);
    };

    // Debounce the date range so manually typed years don't trigger the
    // expensive fetch/processing pipeline on every keystroke. Typing "2025"
    // momentarily produces year 20 etc., which would otherwise run a fetch and
    // generate a key array spanning ~2000 years and freeze the UI.
    const [debouncedDateRange, setDebouncedDateRange] = useState<DateRange>(dateRange);
    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedDateRange(dateRange), 400);
        return () => clearTimeout(timeoutId);
    }, [dateRange.start, dateRange.end]);

    // Main data fetching effect
    useEffect(() => {
        if (hasServiceTypes !== true) return;

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const db = getDatabase();

                // Fetch buildings summary
                const buildingsSummaryRef = dbRef(db, 'buildingsSummary');
                const buildingsSummarySnapshot = await get(buildingsSummaryRef);

                let buildingsData: BuildingSummary[] = [];
                if (buildingsSummarySnapshot.exists()) {
                    const summaryData = buildingsSummarySnapshot.val();
                    buildingsData = Object.entries(summaryData).map(([key, value]: [string, any]) => ({
                        key,
                        ...value
                    }));
                    setBuildings(buildingsData);
                }

                // Set default building types if none selected
                if (selectedBuildingTypes.length === 0) {
                    setSelectedBuildingTypes([...ANALYSIS_BUILDING_TYPES]);
                    setSelectedBuildingTypeOptions(buildingTypeLabelOptions(ANALYSIS_BUILDING_TYPES));
                }

                // Fetch services data
                const servicesRef = dbRef(db, 'buildingServices');
                const snapshot = await get(servicesRef);

                if (!snapshot.exists()) {
                    console.error('No data available in the specified path');
                    setIsLoading(false);
                    return;
                }

                const data = snapshot.val();
                setServicesData(data);
                const servicesConfigsData = data.servicesConfig || {};
                setServicesConfigs(servicesConfigsData);

                // Process service types
                const typesObj = data.servicesTypes || {};
                const filteredTypesObj = Object.fromEntries(
                    Object.entries(typesObj).filter(([key, value]) => key !== 'generalComment')
                );
                const typesArray: ServiceType[] = Object.entries(filteredTypesObj).map(([key, value]: [string, any]) => ({
                    ...value,
                    key: key
                }));
                setServiceTypes(typesArray);

                // Set service titles
                const titles: Record<string, string> = {};
                Object.entries(typesObj).forEach(([key, value]: [string, any]) => {
                    titles[key] = value.title || key;
                });
                setServiceTitles(titles);

                // Extract and process log entries
                const entries = extractServiceLogEntries(
                    data,
                    debouncedDateRange,
                    buildingsData,
                    selectedBuildingTypes,
                    selectedUserGroup,
                    false,
                    selectedBuildingKeys
                );
                setLogEntries(entries);

                // Extract and process comment entries
                const comments = extractServiceLogEntries(
                    data,
                    debouncedDateRange,
                    buildingsData,
                    selectedBuildingTypes,
                    selectedUserGroup,
                    true,
                    selectedBuildingKeys
                );
                setCommentEntries(comments);

                // Extract individual visits
                const visits = extractIndividualVisits(entries);
                setIndividualVisits(visits);

                // Transform data for chart
                const transformedData = transformDataForChart(
                    entries,
                    timeGrouping,
                    buildingsData,
                    debouncedDateRange
                );
                setServiceData(transformedData);

                // Fetch user groups
                const groupsSnapshot = await get(dbRef(db, 'users/groups'));
                if (groupsSnapshot.exists()) {
                    const groupsData = groupsSnapshot.val();
                    const groups: UserGroup[] = Object.entries(groupsData).map(([key, value]: [string, any]) => ({
                        key,
                        name: value.name || key
                    }));
                    setUserGroups(groups);
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [hasServiceTypes, selectedBuildingTypes, debouncedDateRange.start, debouncedDateRange.end, timeGrouping, selectedUserGroup, selectedBuildingKeys, reloadCount]);

    // Helper functions for summary data
    const getSelectedGroupName = (): string => {
        if (!selectedUserGroup) return "alla företag";
        const group = userGroups.find(g => g.key === selectedUserGroup);
        return group ? group.name : selectedUserGroup;
    };

    const getCompanyName = (companyKey: string): string => {
        if (companyKey === 'unknown') return 'Okänd';
        const group = userGroups.find(g => g.key === companyKey);
        return group ? group.name : companyKey;
    };

    const getTotalVisitsCount = (): number => {
        return individualVisits.length;
    };

    const getVisitsByCompany = (): Record<string, number> => {
        const visitsByCompany: Record<string, number> = {};

        individualVisits.forEach(entry => {
            const building = buildings.find(b => b.key === entry.buildingKey);
            let companyKey = 'unknown';
            if (building && building.responsibleGroups && building.responsibleGroups.length > 0) {
                companyKey = building.responsibleGroups[0];
            }

            visitsByCompany[companyKey] = (visitsByCompany[companyKey] || 0) + 1;
        });

        return visitsByCompany;
    };

    // Prepare summary data
    const buildingTypeSummaryData = getServicesSummary(
        logEntries,
        serviceTypes,
        serviceTitles,
        buildings,
        buildingTypeOptions,
        'buildingType'
    );

    const companySummaryData = getServicesSummary(
        logEntries,
        serviceTypes,
        serviceTitles,
        buildings,
        userGroups,
        'company'
    );

    const collectedValuesSummary = getCollectedValuesSummary(logEntries, serviceTypes);

    const isServiceEnabledForBuilding = (buildingKey: string, serviceType: string): boolean => {
        const buildingConfig = servicesConfigs[buildingKey];
        if (!buildingConfig) return false;

        const serviceConfig = buildingConfig[serviceType];
        if (!serviceConfig) return false;

        return serviceConfig.enabled || false;
    };

    const getServiceDisplayValue = (buildingKey: string, serviceType: string, value: number): string => {
        if (!isServiceEnabledForBuilding(buildingKey, serviceType)) {
            return '-';
        }
        return value.toString();
    };

    if (hasServiceTypes !== true) {
        return (
            <>
            <BreadcrumbComponent items={[{ text: 'Analys' }]} />
            <div className="analysis-container">
                <h1 className="analysis-title page-title">Analys</h1>
                {hasServiceTypes === false && (
                    <div className="analysis-card analysis-empty-card std-box dropshadow">
                        <h2 className="summary-title">Ingen analys tillgänglig</h2>
                        <p className="text-muted" style={{ margin: 0 }}>
                            Det finns inga tjänster konfigurerade för den här instansen, så det finns inget att analysera.
                        </p>
                    </div>
                )}
            </div>
            </>
        );
    }

    // The filters, shared by both tabs; Jämför år adds its År picker to the row
    const renderAnalysisControls = (extraControls?: React.ReactNode) => (
        <AnalysisControls
            buildingTypeOptions={buildingTypeOptions}
            selectedBuildingTypeOptions={visibleSelectedBuildingTypeOptions}
            onBuildingTypeChange={setSelectedBuildingTypeOptions}
            selectedBuildingTypes={selectedBuildingTypes}
            onSelectedBuildingTypesChange={setSelectedBuildingTypes}
            selectedUserGroup={selectedUserGroup}
            onUserGroupChange={setSelectedUserGroup}
            userGroups={userGroups}
            timeGrouping={timeGrouping}
            onTimeGroupingChange={setTimeGrouping}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            buildingOptions={buildingFilter.options}
            selectedBuildings={buildingFilter.selected}
            onSelectedBuildingKeysChange={setSelectedBuildingKeys}
            buildingNote={unmatchedNote(buildingFilter)}
            showTimeGrouping={view === 'overview'}
            showDateRange={view === 'overview'}
            extraControls={extraControls}
        />
    );

    return (
        <>
        <BreadcrumbComponent items={[{ text: 'Analys' }]} />
        <div className="analysis-container">
            <h1 className="analysis-title page-title">Besöksanalys per företag</h1>

            <div className="analysis-tabs" role="tablist" aria-label="Analysvy">
                <button
                    type="button"
                    role="tab"
                    aria-selected={view === 'overview'}
                    className={`analysis-tab ${view === 'overview' ? 'active' : ''}`}
                    onClick={() => selectView('overview')}
                >
                    Översikt
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={view === 'compare'}
                    className={`analysis-tab ${view === 'compare' ? 'active' : ''}`}
                    onClick={() => selectView('compare')}
                >
                    Jämför år
                </button>
                {SHOW_ROUTES_TAB && (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'routes'}
                        className={`analysis-tab ${view === 'routes' ? 'active' : ''}`}
                        onClick={() => selectView('routes')}
                    >
                        Resvägar
                    </button>
                )}
            </div>

            {/* Main Analysis Card with Controls and Chart; Jämför år and Resvägar lay out their own cards.
                Resvägar has its own Företag and day pickers instead of the shared filters. */}
            {view === 'routes' ? (
                <RoutesView
                    isLoading={isLoading}
                    servicesData={servicesData}
                    buildings={buildings}
                    users={userList}
                    userGroups={userGroups}
                    serviceTitles={serviceTitles}
                />
            ) : view === 'compare' ? (
                <YearComparisonView
                    renderControls={renderAnalysisControls}
                    isLoading={isLoading}
                    servicesData={servicesData}
                    buildings={buildings}
                    buildingTypes={selectedBuildingTypes}
                    userGroup={selectedUserGroup}
                    buildingKeys={selectedBuildingKeys}
                    emptyMessage={buildingFilter.noneMatch ? NONE_MATCH_MESSAGE : null}
                    serviceTypes={serviceTypes}
                    timeGrouping={timeGrouping}
                    onTimeGroupingChange={setTimeGrouping}
                />
            ) : (
                <div className="analysis-card analysis-main-card std-box dropshadow">
                    {renderAnalysisControls()}

                    <AnalysisChart
                        isLoading={isLoading}
                        serviceData={serviceData}
                        timeGrouping={timeGrouping}
                        getCompanyName={getCompanyName}
                        getSelectedGroupName={getSelectedGroupName}
                        selectedUserGroup={selectedUserGroup}
                        emptyMessage={buildingFilter.noneMatch ? NONE_MATCH_MESSAGE : null}
                    />
                </div>
            )}

            {view === 'overview' && (<>

            <br/>

            {/* Building Type Summary Section */}
            {serviceTypes.length > 0 && (
                <ServicesSummarySection
                    title="Sammanfattning - Utförda tjänster per byggnadstyp"
                    data={buildingTypeSummaryData.data}
                    columns={buildingTypeSummaryData.columns}
                    pieChartData={buildingTypeSummaryData.pieData}
                    pieChartTitle="Fördelning per byggnadstyp"
                    colors={SERVICE_COLORS}
                    totalEntries={logEntries.length}
                    selectedUserGroup={selectedUserGroup}
                    getSelectedGroupName={getSelectedGroupName}
                    showColumnsWithZeroEntries={true}
                />
            )}

            {/* Company Summary Section */}
            {serviceTypes.length > 0 && (
                <ServicesSummarySection
                    title="Sammanfattning - Utförda tjänster per företag"
                    data={companySummaryData.data}
                    columns={companySummaryData.columns}
                    pieChartData={companySummaryData.pieData}
                    pieChartTitle="Fördelning per företag"
                    colors={SERVICE_COLORS}
                    totalEntries={logEntries.length}
                    selectedUserGroup={selectedUserGroup}
                    getSelectedGroupName={getSelectedGroupName}
                    showColumnsWithZeroEntries={false}
                />
            )}

            <br/>

            <h2 style={{marginTop: '20px'}}>Sammanställning</h2>

            {/* Main Summary Card */}
            <div className="analysis-card std-box dropshadow">
                <h2 className="summary-title">Byggnadsbesök</h2>
                <p>Totalt {getTotalVisitsCount()} byggnadsbesök </p>
            </div>

            <div className="analysis-card std-box dropshadow">
                <h2 className="summary-title">Besök per företag</h2>
                {Object.entries(getVisitsByCompany()).map(([companyKey, visits]) => (
                    <p key={companyKey} style={{ margin: '2px 0' }}>
                        {getCompanyName(companyKey)}: {visits} besök
                    </p>
                ))}
            </div>

            <div className="analysis-card std-box dropshadow">
                <h2 className="summary-title">Totala insamlade mängder</h2>
                {collectedValuesSummary.map((summary) => (
                    summary && <p key={summary.serviceType} style={{ margin: '2px 0' }}>
                        {summary.serviceName}: {summary.totalValue.toLocaleString('sv-SE')} {summary.unit}
                        {summary.entryCount > 0 && (
                            <span style={{ color: '#666', fontSize: '0.9em' }}>
                                {' '}({summary.entryCount} registreringar)
                            </span>
                        )}
                    </p>
                ))}
            </div>

            <br/>

            {/* Combined Toplist */}
            <div className="analysis-card std-box dropshadow">
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                    <h2 className="summary-title" style={{ margin: 0 }}>Topplista byggnader</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label htmlFor="sort-select" style={{ fontSize: '14px', color: '#666' }}>
                            Sortera på:
                        </label>
                        <select
                            id="sort-select"
                            className="analysis-sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            style={{
                                padding: '4px 8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="visits">Besök</option>
                            <option value="compost">Kompost</option>
                            <option value="wasteBarrels">Latrintunnor</option>
                            <option value="deposit">Pant</option>
                            <option value="solarControl">Solcellskontroll</option>
                            <option value="sacks">Sopsäckar</option>
                            <option value="cleaning">Städning</option>
                            <option value="tanks">Tankar</option>
                        </select>
                    </div>
                </div>

                {/* Scrolls sideways on small screens instead of widening the page. */}
                <div className="analysis-toplist-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                    <tr style={{ borderBottom: '2px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Byggnad</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Logg</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Besök</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Sopsäckar</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Tankar</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Städning</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Solcellskontroll</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Pant</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Latrintunnor</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Kompost</th>
                    </tr>
                    </thead>
                    <tbody>
                    {combinedTopList.slice(0, 10).map((item, index) => (
                        <tr key={item.key} style={{
                            borderBottom: '1px solid #f5f5f5',
                            backgroundColor: index % 2 === 1 ? '#f9f9f9' : 'transparent'
                        }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    {item.name}
                                </button>
                            </td>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingLogClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    Detaljer
                                </button>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{item.visits}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectGarbage', item.sacks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'toiletPumpout', item.tanks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'generalCleaning', item.cleaning)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'solarCheckTankAndFan', item.solarControl)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectCansSeparately', item.deposit)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'handleWasteBarrels', item.wasteBarrels)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{getServiceDisplayValue(item.key, 'handleCompost', item.compost)}</td>
                        </tr>
                    ))}
                    {combinedTopList.length > 10 && isToplistCollapsed && (
                        <tr>
                            <td
                                colSpan={10}
                                onClick={toggleToplist}
                                style={{
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    color: COLORS.primaryDark,
                                    padding: '12px',
                                    borderTop: '2px solid #f0f0f0',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                Visa alla {combinedTopList.length} byggnader
                            </td>
                        </tr>
                    )}
                    {!isToplistCollapsed && combinedTopList.slice(10).map((item, index) => (
                        <tr key={item.key} style={{
                            borderBottom: '1px solid #f5f5f5',
                            backgroundColor: (index + 10) % 2 === 1 ? '#f9f9f9' : 'transparent'
                        }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    {item.name}
                                </button>
                            </td>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingLogClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    Detaljer
                                </button>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{item.visits}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectGarbage', item.sacks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'toiletPumpout', item.tanks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'generalCleaning', item.cleaning)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'solarCheckTankAndFan', item.solarControl)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectCansSeparately', item.deposit)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'handleWasteBarrels', item.wasteBarrels)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{getServiceDisplayValue(item.key, 'handleCompost', item.compost)}</td>
                        </tr>
                    ))}
                    {!isToplistCollapsed && (
                        <tr>
                            <td
                                colSpan={10}
                                onClick={toggleToplist}
                                style={{
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    color: COLORS.primaryDark,
                                    padding: '12px',
                                    borderTop: '2px solid #f0f0f0',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                Dölj
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Data Tables Section */}
            <DataTablesSection
                serviceTypes={serviceTypes}
                individualVisits={individualVisits}
                logEntries={logEntries}
                commentEntries={commentEntries}
                buildings={buildings}
                users={users || []}
                serviceTitles={serviceTitles}
                selectedUserGroup={selectedUserGroup}
                getSelectedGroupName={getSelectedGroupName}
            />

            </>)}

            {/* Building Logs Modal */}
            <BuildingLogsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                building={selectedBuilding}
                logEntries={logEntries}
                commentEntries={commentEntries}
                individualVisits={individualVisits}
                users={users || []}
                serviceTypes={serviceTypes}
                serviceTitles={serviceTitles}
                canCorrect={isAdmin}
                onCorrected={() => setReloadCount(count => count + 1)}
            />
        </div>
        </>
    );
};

export default AnalysisPage;